import React, { useState, useMemo, useEffect } from 'react'
import _ from 'lodash'
import SummaryCodeAmountChips from "../PayCalendarDetailsModal/SummaryCodeAmountChips";
import { formatCurrency } from '../../utils/commonutils';

const SortableHeader = ({ name, column, order, handleSort, children }) => {
    const isActive = column === name;
    const direction = order ? 'down' : 'up';

    return (
        <th className={`cursorPointer ${isActive ? 'active' : ''}`} onClick={() => handleSort(name)}>
            <div>
                {children}
                {isActive && <i className={`sort amount ${direction} icon ml-05`}></i>}
            </div>
        </th>
    );
};

const WriteoffTableData = (props) => {
    const { reportData, page, getPracticeName, eventClick } = props;
    const [column, setColumn] = useState(null);
    const [order, setOrder] = useState(true); // true for 'asc', false for 'desc'
    const [data, setData] = useState(reportData);
    const [selectedWoCode, setSelectedWoCode] = useState(null);

    useEffect(() => {
        let updateData = reportData;
        if (column) {
            updateData = _.sortBy(updateData, [column]);
            if (!order) {
                updateData.reverse();
            }
        }
        setData(updateData);
    }, [reportData, column, order]);

    useEffect(() => {
        setSelectedWoCode(null);
    }, [reportData]);

    const handleSort = (clickedColumn) => {
        if (column !== clickedColumn) {
            setColumn(clickedColumn);
            setOrder(true);
        } else {
            setOrder(!order);
        }
    };

    const sortedData = useMemo(() => {
        if (!column) return data;
        const sorted = _.sortBy(data, [column]);
        return order ? sorted : sorted.reverse();
    }, [data, column, order]);

    const filteredData = useMemo(() => {
        if (!selectedWoCode) return sortedData;
        return sortedData.filter(item => item.Writeoff_Code === selectedWoCode);
    }, [sortedData, selectedWoCode]);

    const totalPrice = useMemo(() => filteredData.reduce((sum, item) => sum + item.price, 0), [filteredData]);

    const getHeader = () => {
        return (
            <tr>
                {page === 'bGoal' ? (
                    <SortableHeader name="drName" column={column} order={order} handleSort={handleSort}>DrName</SortableHeader>
                ) : (
                    <SortableHeader name="practice" column={column} order={order} handleSort={handleSort}>Practice</SortableHeader>
                )}
                <SortableHeader name="patientId" column={column} order={order} handleSort={handleSort}>Patient#</SortableHeader>
                {eventClick === 'month' &&
                    <>
                        <SortableHeader name="dateOfService" column={column} order={order} handleSort={handleSort}>Date Of Service</SortableHeader>
                        <SortableHeader name="DateEntry" column={column} order={order} handleSort={handleSort}>Entry Date</SortableHeader>
                    </>
                }
                <SortableHeader name="CarrierName" column={column} order={order} handleSort={handleSort}>Carrier</SortableHeader>
                <SortableHeader name="Remark_3" column={column} order={order} handleSort={handleSort}>Procedure Code</SortableHeader>
                <SortableHeader name="Writeoff_Code" column={column} order={order} handleSort={handleSort}>WO Code</SortableHeader>
                <SortableHeader name="price" column={column} order={order} handleSort={handleSort}>WO Amount</SortableHeader>
                <SortableHeader name="Writeoff_Note" column={column} order={order} handleSort={handleSort}>WO Note</SortableHeader>
                <SortableHeader name="Writeoff_Description" column={column} order={order} handleSort={handleSort}>WO Description</SortableHeader>
            </tr>
        );
    };

    const tableBody = () => {
        if (!sortedData || sortedData.length === 0) {
            return (
                <tbody>
                    <tr>
                        <td colSpan="10">No Record Found</td>
                    </tr>
                </tbody>
            );
        }

        return (
            <tbody>
                {filteredData.map((item, index) => (
                    <tr key={item.id || index}>
                        <td>{page === 'bGoal' ? item.drName : item.practice ? getPracticeName(item.practice) : item.practiceName}</td>
                        <td>{item.patientId}</td>
                        {eventClick === 'month' && <td>{item.dateOfService}</td>}
                        {eventClick === 'month' && <td>{item.DateEntry}</td>}
                        <td>{item.CarrierName}</td>
                        <td>{item.Remark_3}</td>
                        <td>{item.Writeoff_Code}</td>
                        <td>{formatCurrency(item.price)}</td>
                        <td>{item.Writeoff_Note}</td>
                        <td>{item.Writeoff_Description}</td>
                    </tr>
                ))}
                <tr key="total">
                    <td colSpan={eventClick === 'month' ? 7 : 5}><strong>Total</strong></td>
                    <td colSpan="3"><strong>{formatCurrency(totalPrice)}</strong></td>
                </tr>
            </tbody>
        );
    };

    return (
        <>
            <div style={{position: 'sticky', top: 0, background: '#fff', zIndex: 2, paddingBottom: '0.5rem'}}>
                <SummaryCodeAmountChips
                    title="Write Off codes summary:"
                    name="Writeoff_Code"
                    value="price"
                    data={reportData ?? []}
                    selectedCode={selectedWoCode}
                    onCodeClick={setSelectedWoCode}
                />
            </div>
            <div className="table-responsive FreezeTable" style={{maxHeight: '50vh'}}>
                <table className="table table-striped table-hover my-0">
                    <thead>
                        {getHeader()}
                    </thead>
                    {tableBody()}
                </table>
            </div>
        </>
    );
}

export default WriteoffTableData;
